import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  EmojiEvents as RankingIcon,
  EmojiEvents as ContestIcon,
  WorkspacePremium as CertificateIcon,
  School as LearningIcon,
  Description as DescriptionIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  KeyboardDoubleArrowLeft as CollapseIcon,
  KeyboardDoubleArrowRight as ExpandIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import EncycGamesLogo from '@components/layout/EncycGamesLogo';
import SidebarAccountFooter from '@components/layout/SidebarAccountFooter';
import { brandColors } from '@config/brandTokens';
import { isFeatureEnabled } from '@config/releaseProfile';

/**
 * Shared player portal sidebar.
 * EncycGames 1.0 hides non-contest items via release profile — modules stay in the codebase.
 */
interface PlayerSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showCollapseToggle?: boolean;
}

const PlayerSidebar: React.FC<PlayerSidebarProps> = ({
  collapsed = false,
  onToggleCollapse,
  showCollapseToggle = false,
}) => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const is = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isCoursesView = location.pathname.startsWith('/player/learning/courses') || location.pathname === '/player/learning';
  const isRoadmapView = location.pathname.startsWith('/player/learning/roadmap') || new URLSearchParams(location.search).get('view') === 'roadmap';

  const showDashboard = isFeatureEnabled('playerDashboard');
  const showLearning = isFeatureEnabled('playerLearning');
  const showRanking = isFeatureEnabled('playerRanking');
  const showContests = isFeatureEnabled('playerContests');
  const showTraining = isFeatureEnabled('playerTrainingEval');
  const showReports = isFeatureEnabled('playerReports');
  const showAi = isFeatureEnabled('playerAiAssistant');
  const showPeer = isFeatureEnabled('playerPeerBenchmark');
  const showCertificates = isFeatureEnabled('playerCertificates');

  const link = (path: string, label: string, Icon: React.ComponentType<{ sx?: any }>, extraMatch?: boolean) => {
    const selected = extraMatch ? is(path) || location.pathname.includes(path) : is(path);
    return (
      <ListItem disablePadding key={path}>
        <ListItemButton
          selected={selected}
          onClick={() => navigate(path)}
          sx={{
            color: '#ecf0f1',
            bgcolor: selected ? '#34495e' : 'transparent',
            '&:hover': { bgcolor: '#34495e' },
            '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
          }}
        >
          <ListItemIcon sx={{ color: selected ? '#ff9800' : '#ecf0f1', minWidth: 56 }}>
            <Icon />
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: selected ? 700 : 600,
                color: selected ? '#ff9800' : '#ecf0f1',
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box sx={{ bgcolor: brandColors.navy, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ bgcolor: brandColors.navy, minHeight: '56px !important', gap: 1, px: collapsed ? 1 : 2 }}>
        <EncycGamesLogo
          width={collapsed ? 44 : 180}
          homePath="/player/contests"
          sx={{
            mr: collapsed ? 0 : 0.5,
            overflow: 'hidden',
            ...(collapsed && {
              '& img': { objectFit: 'cover', objectPosition: 'left center' },
            }),
          }}
        />
        {!collapsed && (
          <Typography variant="caption" noWrap component="div" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, ml: 'auto' }}>
            Player Portal
          </Typography>
        )}
        {showCollapseToggle && onToggleCollapse && (
          <IconButton
            size="small"
            onClick={onToggleCollapse}
            sx={{ ml: 'auto', color: '#ffffff' }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>
        )}
      </Toolbar>
      <List sx={{ flexGrow: 1 }}>
        {showDashboard && link('/player/dashboard', 'Dashboard', DashboardIcon)}
        {showLearning && link('/player/learning/courses', 'Learning', LearningIcon, true)}
        {showLearning && !collapsed && (
          <ListItem disablePadding>
            <ListItemButton
              selected={isCoursesView}
              onClick={() => navigate('/player/learning/courses')}
              sx={{
                color: '#ecf0f1',
                pl: 7,
                bgcolor: isCoursesView ? '#34495e' : 'transparent',
                '&:hover': { bgcolor: '#34495e' },
                '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
              }}
            >
              <ListItemIcon sx={{ color: isCoursesView ? '#ff9800' : '#ecf0f1', minWidth: 36 }}>
                <LearningIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Courses"
                primaryTypographyProps={{
                  fontSize: '0.85rem',
                  fontWeight: isCoursesView ? 700 : 600,
                  color: isCoursesView ? '#ff9800' : '#ecf0f1',
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        {showLearning && !collapsed && (
          <ListItem disablePadding>
            <ListItemButton
              selected={isRoadmapView}
              onClick={() => navigate('/player/learning/roadmap')}
              sx={{
                color: '#ecf0f1',
                pl: 7,
                bgcolor: isRoadmapView ? '#34495e' : 'transparent',
                '&:hover': { bgcolor: '#34495e' },
                '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
              }}
            >
              <ListItemIcon sx={{ color: isRoadmapView ? '#ff9800' : '#ecf0f1', minWidth: 36 }}>
                <TimelineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Roadmap"
                primaryTypographyProps={{
                  fontSize: '0.85rem',
                  fontWeight: isRoadmapView ? 700 : 600,
                  color: isRoadmapView ? '#ff9800' : '#ecf0f1',
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        {showRanking && link('/player/ranking-overview', 'Learn and Rank', RankingIcon)}
        {showContests && link('/player/contests', 'Contests', ContestIcon)}
        {showTraining && link('/player/performance', 'Training Evaluation', AssessmentIcon, true)}
        {showReports && link('/player/my-reports', 'Reports', DescriptionIcon)}
        {showAi && link('/player/recommendations', 'AI Assistant', RecommendationsIcon)}
        {showPeer && link('/player/compare-peers', 'Peer Benchmark', CompareIcon)}
        {showCertificates && link('/player/certificates', 'Certificates', CertificateIcon)}
      </List>
      <SidebarAccountFooter
        collapsed={collapsed}
        variant="dark"
        onLogout={handleLogout}
        links={{
          profile: '/player/profile',
          subscriptions: '/player/profile?tab=subscription',
          invoices: '/player/invoices',
        }}
      />
    </Box>
  );
};

export default PlayerSidebar;
